<template>
  <div class="auto-message">
    <PageHeader 
      title="자동 문자" 
      description="고객에게 더욱 더 쉽게 문자를 보내세요." 
      :margin-bottom="true"
    />

    <AppLoading v-if="loading" text="메시지 목록을 불러오고 있습니다..." />

    <div v-else class="content-container" ref="contentEl">
        <!-- 필터 영역 (툴바 한 줄) -->
        <div class="toolbar-row" ref="toolbarEl">
          <div class="filter-item">
            <el-input
              v-model="filters.search"
              placeholder="통합검색"
              clearable
              style="width: 240px"
              @keyup.enter="loadMessages"
            />
          </div>
          <div class="action-buttons">
            <el-button type="primary" :loading="loading" @click="loadMessages">검색</el-button>
            <el-button plain @click="openCreate">신규 등록</el-button>
            <el-button plain :disabled="selectedRows.length === 0" @click="handleExportExcel">액셀 출력</el-button>
            <el-button
              plain
              :loading="trackingStatusLoading"
              @click="refreshTrackingStatuses"
            >
              상태 조회
            </el-button>
            <el-button v-if="selectedRows.length > 0" @click="handleBulkDelete">선택 삭제</el-button>
          </div>
        </div>

        <!-- 목록 영역 -->
        <div class="list-section">
          <el-table :data="rows" :height="tableHeight" border style="width: 100%" class="data-table" @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="50" />
            <el-table-column prop="recipient_phone" label="수신자" width="115" show-overflow-tooltip />
            <el-table-column prop="customer_name" label="고객명" width="80" show-overflow-tooltip >
              <template #default="{ row }">
                {{ row.customer_name || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="product_name" label="상품명" width="140" show-overflow-tooltip />
            <el-table-column prop="order_number" label="주문번호" width="100" show-overflow-tooltip>
              <template #default="{ row }">
                {{ row.order_number || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="진행 플래그" min-width="430">
              <template #default="{ row }">
                <div class="flag-list">
                  <el-tag v-for="flag in flagDefs" :key="flag.key"
                          :type="row[flag.key] ? 'success' : 'info'"
                          :effect="row[flag.key] ? 'dark' : 'plain'"
                          size="small"
                          class="flag-tag"
                          :class="{ 'flag-disabled': isFlagDisabled(row, flag.key) }"
                          :title="disabledReason(row, flag.key)"
                          @click="handleFlagClick(row, flag.key)">
                    {{ flag.label }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="운송장" width="135" align="center">
              <template #default="{ row }">
                <div 
                  v-if="row.tracking_number" 
                  class="tracking-cell"
                  @click="copyTrackingNumber(row.tracking_number)"
                  title="클릭하여 복사">
                  <span class="tracking-number">{{ row.tracking_number }}</span>
                  <img src="@/assets/icon_content_copy.svg" alt="복사" class="copy-icon" />
                </div>
                <div v-else class="no-tracking">-</div>
              </template>
            </el-table-column>
            <el-table-column label="상태" width="80" align="center">
              <template #default="{ row }">
                <div
                  :class="[
                    'tracking-status-cell',
                    { 'is-error': isTrackingStatusError(row) },
                    trackingStatusClass(row)
                  ]"
                >
                  <span class="tracking-status-value">{{ trackingStatusText(row) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="배송흐름" width="80" align="center">
              <template #default="{ row }">
                <span :class="deliveryStatusClass(row)">{{ row.delivery_status || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="조회" width="75" align="center">
              <template #default="{ row }">
                <el-button 
                  size="small" 
                  type="primary" 
                  :disabled="!row.tracking_number"
                  @click="openDeliveryTracking(row.tracking_number)">
                  조회
                </el-button>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="생성일" width="100" align="center">
              <template #default="{ row }">
                {{ formatYMD(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="액션" width="130" fixed="right" align="center">
              <template #default="{ row }">
                <div class="action-buttons">
                  <el-button size="small" type="primary" @click="openEdit(row)">수정</el-button>
                  <el-button size="small" type="danger" @click="handleDelete(row)">삭제</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination" ref="paginationEl">
            <el-pagination
              background
              layout="prev, pager, next, sizes, total"
              :current-page="pagination.page"
              :page-size="pagination.size"
              :page-sizes="[10, 20, 50]"
              :total="pagination.total"
              @size-change="handleSizeChange"
              @current-change="handlePageChange"
            />
          </div>
        </div>
        <!-- 선택 항목 액션 바 -->
        <div v-if="selectedRows.length > 0" class="selection-action-bar">
          <span class="selection-info">{{ selectedRows.length }}개 항목 선택됨</span>
          <div class="selection-buttons">
            <el-button @click="clearSelection">취소</el-button>
            <el-button @click="handleBulkDelete">선택 항목 삭제</el-button>
          </div>
        </div>
        <!-- 등록/수정 다이얼로그 -->
        <el-dialog
          v-model="formVisible"
          :title="formMode === 'create' ? '주문 메시지 등록' : '주문 메시지 수정'"
          width="560px"
        >
          <el-form :model="formModel" label-position="top">
            <el-row :gutter="16">
              <el-col :xs="24" :sm="8">
                <el-form-item label="수신자 전화번호" required>
                  <el-input
                    v-model="formModel.recipient_phone"
                    placeholder="숫자만"
                    type="tel"
                    @input="handleFormPhoneInput"
                  />
                </el-form-item>
              </el-col>

              <el-col :xs="24" :sm="8">
                <el-form-item label="상품명" required>
                  <el-input v-model="formModel.product_name" placeholder="상품명" />
                </el-form-item>
              </el-col>

            </el-row>

            <el-row :gutter="16">
              <el-col :xs="24" :sm="8">
                <el-form-item label="주문번호">
                  <el-input v-model="formModel.order_number" placeholder="주문번호(선택)" />
                </el-form-item>
              </el-col>

              <el-col :xs="24" :sm="8">
                <el-form-item label="운송장번호">
                  <el-input v-model="formModel.tracking_number" placeholder="선택 입력" />
                </el-form-item>
              </el-col>

              <el-col :xs="24" :sm="8">
                <el-form-item label="고객명">
                  <el-input v-model="formModel.customer_name" placeholder="고객명(선택)" />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>

          <template #footer>
            <span class="dialog-footer">
              <el-button @click="formVisible = false">취소</el-button>
              <el-button type="primary" :loading="formSaving" @click="handleSubmitForm">저장</el-button>
            </span>
          </template>
        </el-dialog>
      </div>
    </div>

</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import * as XLSX from 'xlsx';
import AppLoading from '../../components/common/loading.vue';
import PageHeader from '@/components/common/header.vue';
import { getOrderMessages, createOrderMessage, updateOrderMessage, sendOrderMessage, deleteOrderMessage, fetchTrackingStatus } from '@/services/order';

export default {
  name: 'AutoMessage',
  components: { AppLoading, PageHeader },
  setup() {
    const loading = ref(false);
    const rows = ref([]);
    const tableHeight = ref(400);
    const contentEl = ref(null);
    const toolbarEl = ref(null);
    const paginationEl = ref(null);

    const flagDefs = ref([
      { key: 'customs_error', label: '통관오류' },
      { key: 'order_placed', label: '주문완료', group: 'orderStatus' },
      { key: 'cargo_ordered', label: '화물주문 완료', group: 'orderStatus' },
      { key: 'center_arrived', label: '선적대기' },
      { key: 'shipped', label: '통관시작' }
    ]);

    const pagination = ref({ page: 1, size: 20, total: 0 });
    const filters = ref({ search: '' });

    // 다중 선택
    const selectedRows = ref([]);

    // 등록/수정 폼
    const formVisible = ref(false);
    const formMode = ref('create'); // 'create' | 'edit'
    const formSaving = ref(false);
    const editingId = ref(null);
    const formModel = ref({
      recipient_phone: '',
      customer_name: '',
      product_name: '',
      order_number: '',
      tracking_number: ''
    });

    const trackingStatusLoading = ref(false);
    const trackingStatusColorMap = {
      배송완료: 'status-blue',
      배송중: 'status-green',
      반출: 'status-green',
      입항: 'status-green'
    };

    const trackingStatusText = (row) => {
      if (row?.status) return row.status;
      const entry = row?.__trackingStatus;
      if (!entry) return '-';
      if (entry.status === 'error') return '조회 실패';
      if (!entry.status || entry.status === 'unknown') return '-';
      return entry.status;
    };

    const trackingStatusClass = (row) => {
      const text = trackingStatusText(row);
      if (!text || text === '-') return '';
      return trackingStatusColorMap[text] || '';
    };

    const deliveryStatusClass = (row) => {
      const status = row?.delivery_status || '';
      if (!status || status === '-') return '';
      if (['전체입고', '출고확정', '결제대기', '결제완료', '출고완료'].includes(status)) return 'delivery-status-green';
      if (['배송완료'].includes(status)) return 'delivery-status-blue';
      return '';
    };

    const isTrackingStatusError = (row) => {
      return row?.__trackingStatus?.status === 'error';
    };

    const formatYMD = (value) => {
      if (!value) return '';
      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      } catch (e) {
        return String(value).slice(0, 10);
      }
    };

    const loadMessages = async () => {
      loading.value = true;
      try {
        const response = await getOrderMessages({
          page: pagination.value.page,
          size: pagination.value.size,
          search: filters.value.search || undefined
        });
        if (response.success) {
          rows.value = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.items)
              ? response.data.items
              : (response.data || []);
          const total = response.total ?? response.data?.total ?? rows.value.length;
          pagination.value.total = typeof total === 'number' ? total : rows.value.length;
        }
      } catch (err) {
        ElMessage.error(err.response?.data?.message || '목록 조회에 실패했습니다.');
        rows.value = [];
        pagination.value.total = 0;
      } finally {
        loading.value = false;
      }
    };

    const handlePageChange = (page) => {
      pagination.value.page = page;
      loadMessages();
    };

    const handleSizeChange = (size) => {
      pagination.value.size = size;
      pagination.value.page = 1;
      loadMessages();
    };


    const openCreate = () => {
      formMode.value = 'create';
      editingId.value = null;
      formModel.value = {
        recipient_phone: '',
        customer_name: '',
        product_name: '',
        order_number: '',
        tracking_number: ''
      };
      formVisible.value = true;
    };

    const openEdit = (row) => {
      formMode.value = 'edit';
      editingId.value = row.id;
      formModel.value = {
        recipient_phone: row.recipient_phone || '',
        customer_name: row.customer_name || '',
        product_name: row.product_name || '',
        order_number: row.order_number || '',
        tracking_number: row.tracking_number || ''
      };
      formVisible.value = true;
    };

    const handleFormPhoneInput = (val) => {
      const onlyDigits = (val || '').replace(/\D/g, '');
      formModel.value.recipient_phone = onlyDigits;
    };

    const handleSubmitForm = async () => {
      if (!formModel.value.recipient_phone || !formModel.value.product_name) {
        ElMessage.warning('필수 항목을 입력해 주세요.');
        return;
      }
      formSaving.value = true;
      try {
        if (formMode.value === 'create') {
          const payload = { ...formModel.value };
          const res = await createOrderMessage(payload);
          if (!res?.success) throw new Error(res?.message || '등록 실패');
          ElMessage.success(res.message || '등록되었습니다.');
        } else if (editingId.value) {
          const payload = { ...formModel.value };
          const res = await updateOrderMessage(editingId.value, payload);
          if (!res?.success) throw new Error(res?.message || '수정 실패');
          ElMessage.success(res.message || '수정되었습니다.');
        }
        formVisible.value = false;
        await loadMessages();
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || e.message || '요청 처리 중 오류가 발생했습니다.');
      } finally {
        formSaving.value = false;
      }
    };

    const refreshTrackingStatuses = async () => {
      if (trackingStatusLoading.value) {
        ElMessage.info('상태 조회가 이미 진행 중입니다.');
        return;
      }
      const targets = rows.value.filter((row) => row.tracking_number);
      if (targets.length === 0) {
        ElMessage.info('운송장 번호가 있는 항목이 없습니다.');
        return;
      }
      trackingStatusLoading.value = true;
      try {
        const results = await Promise.all(
          targets.map(async (row) => {
            try {
              const response = await fetchTrackingStatus(row.tracking_number);
              if (response?.success) {
                row.status = response.status ?? row.status ?? null;
                row.__trackingStatus = {
                  status: response.status || 'unknown',
                  trackingNumber: response.trackingNumber || row.tracking_number,
                  fetchedAt: new Date().toISOString()
                };
                return true;
              }
              row.__trackingStatus = {
                status: 'error',
                message: response?.message || '조회 실패',
                fetchedAt: new Date().toISOString()
              };
              return false;
            } catch (error) {
              row.__trackingStatus = {
                status: 'error',
                message: error?.response?.data?.message || '조회 중 오류가 발생했습니다.',
                fetchedAt: new Date().toISOString()
              };
              return false;
            }
          })
        );
        const failCount = results.filter((result) => !result).length;
        if (failCount === targets.length) {
          ElMessage.error('운송장 상태 조회에 모두 실패했습니다.');
        } else if (failCount > 0) {
          ElMessage.warning('일부 운송장 상태 조회에 실패했습니다.');
        } else {
          ElMessage.success('운송장 상태 조회를 완료했습니다.');
        }
      } finally {
        trackingStatusLoading.value = false;
      }
    };

    const isTruthyFlag = (val) => {
      return val === true || val === 1 || val === '1' || val === 'true';
    };

    const toggleFlag = (row, key) => {
      if (!row) return;
      if (isFlagDisabled(row, key)) {
        const reason = disabledReason(row, key);
        if (reason) ElMessage.info(reason);
        return;
      }
      // 상호배타 그룹 처리
      const def = flagDefs.value.find(f => f.key === key);
      if (def && def.group && !isTruthyFlag(row[key])) {
        const others = flagDefs.value.filter(f => f.group === def.group && f.key !== key);
        for (const other of others) {
          if (row[other.key]) row[other.key] = false;
        }
      }
      row[key] = !isTruthyFlag(row[key]);
    };

    const handleFlagClick = async (row, key) => {
      // 클릭 시: 컨디션 체크 → API 발송 → 성공 시 토글
      if (isFlagDisabled(row, key)) {
        const reason = disabledReason(row, key);
        if (reason) ElMessage.info(reason);
        return;
      }
      try {
        const payload = {
          type: key,
          recipient_phone: row.recipient_phone,
          customer_name: row.customer_name,
          product_name: row.product_name,
          tracking_number: row.tracking_number
        };
        const res = await sendOrderMessage(row.id, payload);
        if (res?.success) {
          const code = res?.aligo?.result_code;
          const codeStr = code === undefined || code === null ? null : String(code);
          if (codeStr && codeStr !== '1') {
            const msg = res?.aligo?.message || '알리고 발송 실패';
            if (codeStr === '-201') {
              ElMessage.error('알리고 보유건수가 부족합니다. 충전 후 다시 시도해 주세요.');
            } else {
              ElMessage.error(`알리고 오류(${codeStr}): ${msg}`);
            }
            return;
          }
          ElMessage.success('메시지 발송 요청이 접수되었습니다.');
          // 성공 시 기존 토글 로직 적용
          toggleFlag(row, key);
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || '메시지 발송 중 오류가 발생했습니다.');
      }
    };

    const isFlagDisabled = (row, key) => {
      // 0) 이미 TRUE(발송됨) 상태면 재발송/변경 방지
      if (isTruthyFlag(row?.[key])) return true;
      // 1) 주문완료/화물주문 완료 상호 배타: 하나가 켜지면 다른 하나 비활성화
      if (key === 'order_placed' && isTruthyFlag(row?.cargo_ordered) && !isTruthyFlag(row?.order_placed)) return true;
      if (key === 'cargo_ordered' && isTruthyFlag(row?.order_placed) && !isTruthyFlag(row?.cargo_ordered)) return true;
      // 2) 운송장 번호 없으면 출고완료 비활성화
      if (key === 'shipped' && !row?.tracking_number) return true;
      return false;
    };

    const disabledReason = (row, key) => {
      if (isTruthyFlag(row?.[key])) return '이미 발송된 항목입니다';
      if (key === 'order_placed' && row?.cargo_ordered && !row?.order_placed) return '화물주문 완료 상태에서는 주문완료로 변경할 수 없습니다';
      if (key === 'cargo_ordered' && row?.order_placed && !row?.cargo_ordered) return '주문완료 상태에서는 화물주문 완료로 변경할 수 없습니다';
      if (key === 'shipped' && !row?.tracking_number) return '운송장 번호를 업데이트해 주세요';
      return '';
    };

    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          '이 메시지를 삭제하시겠습니까? 되돌릴 수 없습니다.',
          '삭제 확인',
          { confirmButtonText: '삭제', cancelButtonText: '취소', type: 'warning' }
        );
        const res = await deleteOrderMessage(row.id);
        if (res?.success) {
          ElMessage.success('삭제되었습니다.');
          rows.value = rows.value.filter((r) => r.id !== row.id);
          if (pagination.value.total > 0) pagination.value.total -= 1;
        }
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e?.response?.data?.message || '삭제 중 오류가 발생했습니다.');
        }
      }
    };

    const handleBulkDelete = async () => {
      if (selectedRows.value.length === 0) {
        ElMessage.warning('선택된 항목이 없습니다.');
        return;
      }
      try {
        await ElMessageBox.confirm(
          `${selectedRows.value.length}개 항목을 삭제하시겠습니까? 되돌릴 수 없습니다.`,
          '선택 항목 삭제 확인',
          { confirmButtonText: '삭제', cancelButtonText: '취소', type: 'warning' }
        );
        const ids = selectedRows.value.map(row => row.id);
        const deletePromises = ids.map(id =>
          deleteOrderMessage(id).catch(err => {
            console.error(`ID ${id} 삭제 실패:`, err);
            return { success: false };
          })
        );
        const results = await Promise.all(deletePromises);
        const deletedCount = results.filter(res => res?.success).length;
        if (deletedCount > 0) {
          ElMessage.success(`${deletedCount}개 항목이 삭제되었습니다.`);
          rows.value = rows.value.filter((r) => !ids.includes(r.id));
          if (pagination.value.total > 0) pagination.value.total -= deletedCount;
          selectedRows.value = [];
        }
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e?.response?.data?.message || '선택 항목 삭제 중 오류가 발생했습니다.');
        }
      }
    };

    const handleExportExcel = async () => {
      if (selectedRows.value.length === 0) {
        ElMessage.warning('선택된 항목이 없습니다.');
        return;
      }

      try {
        const response = await fetch('/sample.xls');
        if (!response.ok) throw new Error('템플릿 파일을 불러오지 못했습니다.');

        const buffer = await response.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        if (!worksheet) throw new Error('템플릿 시트를 찾을 수 없습니다.');

        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:B1');

        for (let rowIndex = 0; rowIndex <= range.e.r; rowIndex += 1) {
          delete worksheet[XLSX.utils.encode_cell({ c: 0, r: rowIndex })];
          delete worksheet[XLSX.utils.encode_cell({ c: 1, r: rowIndex })];
        }

        selectedRows.value.forEach((row, index) => {
          const targetRow = index;
          worksheet[XLSX.utils.encode_cell({ c: 0, r: targetRow })] = {
            t: 's',
            v: row?.recipient_phone || ''
          };
          worksheet[XLSX.utils.encode_cell({ c: 1, r: targetRow })] = {
            t: 's',
            v: row?.tracking_number || ''
          };
        });

        const lastRow = Math.max(range.e.r, selectedRows.value.length);
        worksheet['!ref'] = XLSX.utils.encode_range({
          s: { c: 0, r: 0 },
          e: { c: Math.max(range.e.c, 1), r: lastRow }
        });

        XLSX.writeFile(workbook, `auto-message-${Date.now()}.xls`, { bookType: 'biff8' });
      } catch (error) {
        ElMessage.error(error?.message || '액셀 출력 중 오류가 발생했습니다.');
      }
    };

    const openDeliveryTracking = (trackingNumber) => {
      if (!trackingNumber) return;
      const encodedTrackingNumber = encodeURIComponent(trackingNumber);
      const baseUrl = `https://dlekdhs1.mycafe24.com/${encodedTrackingNumber}`;
      const url = baseUrl;
      const width = 800;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
      window.open(url, 'deliveryTracking', features);
    };

    const copyText = async (value, successMessage) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        ElMessage.success(successMessage);
      } catch (err) {
        ElMessage.error('복사에 실패했습니다.');
      }
    };

    const copyTrackingNumber = async (trackingNumber) => {
      await copyText(trackingNumber, '운송장 번호가 복사되었습니다.');
    };

    // confirmSend는 개별 태그 클릭 방식으로 대체됨

    const computeTableHeight = () => {
      try {
        const container = contentEl.value;
        const toolbar = toolbarEl.value;
        const pager = paginationEl.value;
        if (!container) return;
        const h = container.clientHeight
          - (toolbar?.offsetHeight || 0)
          - (pager?.offsetHeight || 0)
          - 24; // padding 여유
        if (h > 200) tableHeight.value = h;
      } catch (err) {
        // no-op
      }
    };

    const handleResize = () => {
      computeTableHeight();
    };

    onMounted(async () => {
      await nextTick();
      computeTableHeight();
      window.addEventListener('resize', handleResize);
      loadMessages();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      loading,
      rows,
      flagDefs,
      pagination,
      filters,
      loadMessages,
      handlePageChange,
      handleSizeChange,
      openCreate,
      openEdit,
      formVisible,
      formMode,
      formSaving,
      formModel,
      handleFormPhoneInput,
      handleSubmitForm,
      toggleFlag,
      handleFlagClick,
      isFlagDisabled,
      disabledReason,
      handleDelete,
      openDeliveryTracking,
      copyTrackingNumber,
      formatYMD,
      trackingStatusLoading,
      trackingStatusText,
      trackingStatusClass,
      deliveryStatusClass,
      isTrackingStatusError,
      refreshTrackingStatuses,
      tableHeight,
      contentEl,
      toolbarEl,
      paginationEl,
      selectedRows,
      handleSelectionChange: (selection) => {
        selectedRows.value = selection;
      },
      clearSelection: () => {
        selectedRows.value = [];
      },
      handleBulkDelete,
      handleExportExcel
    };
  }
}
</script>

<style scoped>
.auto-message {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--el-bg-color-page);
}

.content-container {
  flex: 1;
  overflow: hidden;
}

/* naverAds 스타일 차용 */
.toolbar-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--el-border-color-lighter);
  margin-bottom: var(--spacing-xs);
  flex-shrink: 0;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.filter-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  margin-left: auto;
}

.list-section {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.flag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.flag-tag {
  min-width: 72px;
  justify-content: center;
  cursor: pointer;
}

.flag-disabled {
  opacity: 0.45;
  cursor: not-allowed;
  filter: grayscale(0.6);
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-md);
}

.tracking-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  margin: -8px -12px;
  cursor: pointer;
  user-select: none;
  border-radius: 4px;
  transition: all 0.2s;
}

.tracking-cell:hover {
  background-color: var(--el-fill-color-light);
}

.tracking-cell:active {
  background-color: var(--el-fill-color);
}

.copy-icon {
  width: 16px;
  height: 16px;
  opacity: 0.6;
  transition: opacity 0.2s;
  pointer-events: none;
}

.tracking-cell:hover .copy-icon {
  opacity: 1;
}

.no-tracking {
  color: var(--el-text-color-placeholder);
  padding: 8px 12px;
}

.selection-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: var(--el-fill-color-light);
  border-radius: var(--el-border-radius-base);
  margin-bottom: var(--spacing-sm);
  border-left: 4px solid var(--el-color-primary);
}

.selection-info {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-primary);
  font-weight: var(--el-font-weight-medium);
}

.selection-buttons {
  display: flex;
  gap: var(--spacing-sm);
}


.tracking-status-cell.status-green:not(.is-error) .tracking-status-value {
  color: #22c55e;
}

.tracking-status-cell.status-blue:not(.is-error) .tracking-status-value {
  color: #2563eb;
}

.tracking-status-cell.is-error .tracking-status-value {
  color: var(--el-color-danger);
}

.delivery-status-green {
  color: #22c55e;
}

.delivery-status-blue {
  color: #2563eb;
}

@media (max-width: 768px) {
  .content-container { padding: var(--spacing-sm); }
  .selection-action-bar {
    flex-direction: column;
    gap: var(--spacing-sm);
    align-items: flex-start;
  }
  .selection-buttons {
    width: 100%;
  }
}
</style>


